<template>
    <modal @close="$emit('close')" maxWidth="550px" width="95%">
        <div slot="title">{{$t('title')}}</div>
        <div class="container">
            <loading-indicator :clear-background="true" :full-parent="true" :is-loading="isLoading"></loading-indicator>
            <div class="py-3 px-2">
                <div class="d-flex">
                    <input cy-input="promo-code" type="text" name="promo-code" class="form-control" v-model="promoCode" :placeholder="$t('enter_promo_code')">
                    <button cy-button="use" class="btn btn-sm btn-primary flex-shrink-0 ml-2" @click="usePromoCode">{{ $t('use') }}</button>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Modal from './common/Modal.vue';
import promptConfirm from '../mixins/prompt-confirm';
import api from '../mixins/api';
import { EventApplyOffer } from '../bu/models/GA4';
import LoadingIndicator from './common/LoadingIndicator.vue';
import moment from 'moment';
import { getExchangedAmount } from '../bu/CurrencyUtils';

export default {
    components: { Modal, LoadingIndicator },
    props: ['context', 'provider', 'customer'],
    mixins: [promptConfirm, api],
    data() {
        return {
            promoCode: '',
            isLoading: false
        }
    },
    mounted() {
        this.getAvailableOffers();
    },
    computed: {
        ...mapGetters({
            availableOffers: 'offers/offers',
            cartItems: 'cart/items',
            itemTotal: 'cart/itemTotal',
            bookingPassTotal: 'cart/bookingPassTotal',
            serviceTotal: 'cart/serviceTotal',
            offerTotal: 'cart/offerTotal',
            pointsDeductionTotal: 'cart/pointsDeductionTotal',
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        nonEmptyCartItems() {
			return this.cartItems.filter((item) => {
                return item.count > 0;
            });
		}
    },
    methods: {
        ...mapActions({
            getAvailableOffers: 'offers/getAvailableOffers',
            setOffer: 'cart/setOffer'
        }),
        isPromoCodeValid() {
            if (this.promoCode) {
                let isOk = true;
                const offer = this.availableOffers.find(offer => offer.promo_code == this.promoCode);

                const markNotAvailableCallback = () => {
                    this.promptAlert(this.$t('promo_code_not_available', {code: this.promoCode}));
                    this.promoCode = '';
                    isOk = false;
                }

                if (offer) {
                    const conditions = offer?.conditions.map(c => {
                        if (c.extras && typeof c.extras == 'string') {
                            c.extras = JSON.parse(c.extras);
                        }
                        return c;
                    });

                    const minOrderAmountCondition = conditions.find(c => c.type == 'MIN_ORDER_AMOUNT');
                    if (minOrderAmountCondition) {
                        const currentCartAmount = this.itemTotal + this.serviceTotal + this.bookingPassTotal - this.pointsDeductionTotal - this.offerTotal;
                        if (currentCartAmount < minOrderAmountCondition.extras.min) {
                            markNotAvailableCallback();
                        }
                    }

                    const limitedItems = conditions.find(c => c.type == 'LIMITED_ITEMS');
                    if (limitedItems) {
                        if (limitedItems.extras.service_ids.length) {
                            const matched = limitedItems.extras.service_ids.find(id => id == this.context.selectedService.id);
                            if (!matched) {
                                markNotAvailableCallback();
                            }
                        }
                        if (limitedItems.extras.buyables && limitedItems.extras.buyables.length) {
                            const hasMatched = this.nonEmptyCartItems.find(item => {
                                if (item.itemId) {
                                    return limitedItems.extras.buyables.find(buyable => buyable.item_id == item.itemId && buyable.spec_id == item.specId);
                                }
                                return false;
                            });
                            if (!hasMatched) {
                                markNotAvailableCallback();
                            }
                        }
                    }
                    return isOk;
                } else {
                    this.promptAlert(this.$t('no_matched_promo_code'));
                    this.promoCode = '';
                    return false;
                }
            }
        },
        usePromoCode() {
            if (!this.isPromoCodeValid()) {
                return;
            }
            const offer = this.availableOffers.find(offer => offer.promo_code == this.promoCode);
            const dateFormatStyle = 'YYYY-MM-DD HH:mm:ss';
            const start = this.context.selectedDate ? moment.tz(this.context.selectedDate, this.provider.timezone).format(dateFormatStyle) : null;
            const end = this.context.selectedEndDate ? moment.tz(this.context.selectedEndDate, this.provider.timezone).format(dateFormatStyle) : null;
            const levelId = this.customer?.level?.id;
			const serviceId = this.context.selectedService?.id;

            this.isLoading = true;
            this.getOfferDiscountAmount(this.nonEmptyCartItems, start, end, levelId, serviceId, offer, (response, error) => {
                this.isLoading = false;
                if (response?.data && Array.isArray(response.data)) {
                    const data = response.data.find(item => item.offer_type === 'App\\Models\\Offer' && item.offer_id === offer.id);
                    if (data) {
                        const discount = data.discount_amount;
                        offer.discount = discount;
                        this.setOffer({offer});
                        this.promptAlert(
                            this.$t('success_use_promo_code', {
                                code: offer.promo_code,
                                value: this.getExchangedAmount(discount)
                            }),
                            null,
                            'success'
                        )
                        this.trackApplyOffer(offer, discount, true);
                        this.$emit('close');
                    }
                } else if (error) {
                    const message = error.response?.data?.error;
                    if (error.response.status !== 422 && message) {
                        this.promptAlert(error.response.data.error);
                    } else {
                        this.promptAlert(this.$t('no_matched_promo_code'));
                    }
                    this.trackApplyOffer(offer, 0, false);
                }
            });
        },
        trackApplyOffer(offer, discount, isSuccess) {
            window.tracker.track(() => new EventApplyOffer(offer, discount, isSuccess));
        },
        getExchangedAmount(amount) {
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.currency,
                currencyOptions: this.currencyOptions,
            });
        },
    }
}
</script>

<style scoped lang="scss">
</style>

<i18n>
    {
        "en": {
            "title": "Promo code",
            "enter_promo_code": "Please enter promo code",
            "use": "Use",
            "no_matched_promo_code": "No matched promo code",
            "success_use_promo_code": "{code} is used and will get {value} discount",
            "promo_code_not_available": "Promo code {code} is not available"
        },
        "zh": {
            "title": "優惠碼",
            "enter_promo_code": "請輸入優惠碼",
            "use": "使用",
            "no_matched_promo_code": "此優惠碼無效",
            "success_use_promo_code": "將使用優惠碼 {code}，\n折扣 {value} 元至本次消費",
            "promo_code_not_available": "優惠碼 {code} 無法使用"
        }
    }
</i18n>